import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import ListSubheader from '@mui/material/ListSubheader';
import SvgIcon from '@mui/material/SvgIcon';
import { ReactComponent as TrashIcon } from '../assets/icons/trash.svg';
import { ReactComponent as SidebarCrewMgmtIcon } from '../assets/icons/sidebar_crew_management.svg';
import PageHeader from '../components/PageHeader';
import RankStar from '../components/RankStar';
import AddItemButton from '../components/AddItemButton';

import { skillSettingsApi, jobPositionApi, getApiErrorMessage } from '../services/api';
import { SkillTier, SkillDefinition, JobPositionWithIndustry, SkillImportResponse } from '../types';
import { useAuth } from '../auth/AuthContext';
import { useManagementAuth } from '../auth/ManagementAuthContext';

const PRIMARY_BLUE = '#0071e3';

interface EditableSkill {
  localId: string;
  skill_definition_id?: string;
  tier_id: string;
  slot_index: number;
  name: string;
  originalName?: string;
  manualUrl: string;
  originalManualUrl?: string;
  isDeleted: boolean;
}

interface PositionEditSection {
  sectionId: string;
  positionId: string;
  skills: EditableSkill[];
}

let _localIdCounter = 0;
const nextLocalId = () => `loc_${++_localIdCounter}`;
const nextSectionId = () => `sec_${++_localIdCounter}`;

const SkillSettings: React.FC = () => {
  const { storeId } = useAuth();
  const { managementToken } = useManagementAuth();
  const token = managementToken || localStorage.getItem('management_token') || '';

  const [positions, setPositions] = useState<JobPositionWithIndustry[]>([]);
  const [tiers, setTiers] = useState<SkillTier[]>([]);
  const [allDefinitions, setAllDefinitions] = useState<SkillDefinition[]>([]);

  const [loadingPositions, setLoadingPositions] = useState<boolean>(true);
  const [loadingTiers, setLoadingTiers] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // 編集モーダル
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [editSections, setEditSections] = useState<PositionEditSection[]>([]);
  const [isSavingModal, setIsSavingModal] = useState<boolean>(false);
  const [modalError, setModalError] = useState<string | null>(null);

  // CSVインポートモーダル
  const [showImportModal, setShowImportModal] = useState<boolean>(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [importResult, setImportResult] = useState<SkillImportResponse | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  // ─── 職種一覧取得 ────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchPositions = async () => {
      setLoadingPositions(true);
      try {
        if (storeId) {
          setPositions(await jobPositionApi.getActiveWithIndustry(storeId));
        } else {
          setPositions([]);
        }
      } catch {
        setPositions([]);
      } finally {
        setLoadingPositions(false);
      }
    };
    fetchPositions();
  }, [storeId]);

  // ─── tier マスタ取得 ─────────────────────────────────────────────────────
  useEffect(() => {
    const fetchTiers = async () => {
      setLoadingTiers(true);
      try {
        const list = await skillSettingsApi.getSkillTiers(storeId, token);
        setTiers([...list].sort((a, b) => a.sort_order - b.sort_order));
        setError(null);
      } catch (err) {
        setTiers([]);
        setError(getApiErrorMessage(err, 'ランク情報の取得に失敗しました'));
      } finally {
        setLoadingTiers(false);
      }
    };
    if (storeId) fetchTiers();
  }, [storeId, token]);

  // ─── 全スキル定義取得（position_id なし = 全職種分） ─────────────────────
  const fetchAllDefinitions = useCallback(async () => {
    if (!storeId) {
      setAllDefinitions([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const list = await skillSettingsApi.getSkillDefinitions(storeId, token, { active_only: true });
      setAllDefinitions(list);
      setError(null);
    } catch (err) {
      setAllDefinitions([]);
      setError(getApiErrorMessage(err, 'スキル設定の取得に失敗しました'));
    } finally {
      setIsLoading(false);
    }
  }, [storeId, token]);

  useEffect(() => {
    fetchAllDefinitions();
  }, [fetchAllDefinitions]);

  // ─── 表示用: position_id でグルーピング ──────────────────────────────────
  const defsByPositionId = allDefinitions.reduce<Record<string, SkillDefinition[]>>(
    (acc, d) => {
      const pid = d.position_id?.toString() ?? '';
      acc[pid] = [...(acc[pid] ?? []), d];
      return acc;
    },
    {}
  );

  // ─── 編集モーダル ハンドラ ───────────────────────────────────────────────
  const buildEditableSkills = (defs: SkillDefinition[]): EditableSkill[] =>
    defs.map((d) => ({
      localId: nextLocalId(),
      skill_definition_id: d.skill_definition_id,
      tier_id: d.tier?.tier_id ?? '',
      slot_index: d.slot_index ?? 0,
      name: d.name,
      originalName: d.name,
      manualUrl: d.manual_url ?? '',
      originalManualUrl: d.manual_url ?? '',
      isDeleted: false,
    }));

  const handleOpenEdit = () => {
    // allDefinitions を position_id でグループ化し、positions の順に sections を生成
    const pidMap = new Map<string, SkillDefinition[]>();
    for (const d of allDefinitions) {
      const pid = d.position_id?.toString() ?? '';
      if (!pidMap.has(pid)) pidMap.set(pid, []);
      pidMap.get(pid)!.push(d);
    }
    const sections: PositionEditSection[] = positions
      .map((pos) => {
        const pid = pos.id.toString();
        const defs = pidMap.get(pid);
        if (!defs || defs.length === 0) return null;
        return { sectionId: nextSectionId(), positionId: pid, skills: buildEditableSkills(defs) };
      })
      .filter((s): s is PositionEditSection => s !== null);

    setEditSections(sections);
    setModalError(null);
    setShowEditModal(true);
  };

  const handleCloseEdit = () => {
    if (isSavingModal) return;
    setShowEditModal(false);
    setEditSections([]);
    setModalError(null);
  };

  const handleSectionPositionChange = async (sectionId: string, e: SelectChangeEvent) => {
    const newPid = e.target.value;
    if (editSections.some((s) => s.sectionId !== sectionId && s.positionId === newPid)) return;

    let newSkills: EditableSkill[] = [];
    if (storeId && newPid) {
      try {
        const defs = await skillSettingsApi.getSkillDefinitions(storeId, token, {
          position_id: newPid,
          active_only: true,
        });
        newSkills = buildEditableSkills(defs);
      } catch {
        // エラー時は空スキルで続行
      }
    }
    setEditSections((prev) =>
      prev.map((s) =>
        s.sectionId === sectionId ? { ...s, positionId: newPid, skills: newSkills } : s
      )
    );
  };

  const handleAddPositionSection = () => {
    setEditSections((prev) => [
      ...prev,
      { sectionId: nextSectionId(), positionId: '', skills: [] },
    ]);
  };

  const handleSkillNameChange = (sectionId: string, localId: string, name: string) => {
    setEditSections((prev) =>
      prev.map((s) =>
        s.sectionId !== sectionId
          ? s
          : { ...s, skills: s.skills.map((sk) => (sk.localId === localId ? { ...sk, name } : sk)) }
      )
    );
  };

  const handleDeleteSkill = (sectionId: string, localId: string) => {
    setEditSections((prev) =>
      prev.map((s) =>
        s.sectionId !== sectionId
          ? s
          : {
              ...s,
              skills: s.skills
                .map((sk): EditableSkill | null => {
                  if (sk.localId !== localId) return sk;
                  if (!sk.skill_definition_id) return null;
                  return { ...sk, isDeleted: true };
                })
                .filter((sk): sk is EditableSkill => sk !== null),
            }
      )
    );
  };

  const handleAddSkillToTier = (sectionId: string, tier: SkillTier) => {
    setEditSections((prev) =>
      prev.map((s) => {
        if (s.sectionId !== sectionId) return s;
        return {
          ...s,
          skills: [
            ...s.skills,
            {
              localId: nextLocalId(),
              tier_id: tier.tier_id,
              slot_index: -1,
              name: '',
              manualUrl: '',
              isDeleted: false,
            },
          ],
        };
      })
    );
  };

  const handleSkillManualUrlChange = (sectionId: string, localId: string, manualUrl: string) => {
    setEditSections((prev) =>
      prev.map((s) =>
        s.sectionId !== sectionId
          ? s
          : { ...s, skills: s.skills.map((sk) => (sk.localId === localId ? { ...sk, manualUrl } : sk)) }
      )
    );
  };

  // 入力欄に値があるとき、http/https で始まらなければエラー扱い（空はOK＝未設定）。
  const isManualUrlInvalid = (raw: string): boolean => {
    const trimmed = raw.trim();
    if (trimmed === '') return false;
    return !(trimmed.startsWith('http://') || trimmed.startsWith('https://'));
  };

  const handleSaveModal = async () => {
    setModalError(null);

    // 全セクションのマニュアルURLを事前検証（http/https 始まりのみ）。
    for (const section of editSections) {
      for (const sk of section.skills) {
        if (sk.isDeleted) continue;
        if (isManualUrlInvalid(sk.manualUrl)) {
          setModalError('マニュアルURLは http:// または https:// で始めてください');
          return;
        }
      }
    }

    setIsSavingModal(true);
    try {
      for (const section of editSections) {
        if (!section.positionId) continue;

        // 1. 削除
        for (const sk of section.skills.filter((s) => s.isDeleted && s.skill_definition_id)) {
          await skillSettingsApi.deleteSkillDefinition(storeId, sk.skill_definition_id!, token);
        }

        // 2. 既存スキルの更新（名前 or マニュアルURLの変更を検知）
        for (const sk of section.skills.filter((s) => !s.isDeleted && s.skill_definition_id)) {
          if (!sk.name.trim()) continue;
          const nameChanged = sk.name.trim() !== (sk.originalName ?? '');
          const urlChanged = sk.manualUrl.trim() !== (sk.originalManualUrl ?? '');
          if (!nameChanged && !urlChanged) continue;

          const payload: { name?: string; manual_url?: string } = {};
          if (nameChanged) payload.name = sk.name.trim();
          if (urlChanged) payload.manual_url = sk.manualUrl.trim();
          await skillSettingsApi.updateSkillDefinition(storeId, sk.skill_definition_id!, payload, token);
        }

        // 3. 新規作成（tier 別に slot 計算）
        const toCreate = section.skills.filter((s) => !s.skill_definition_id && !s.isDeleted && s.name.trim());
        const tierGroups = new Map<string, EditableSkill[]>();
        for (const sk of toCreate) {
          if (!tierGroups.has(sk.tier_id)) tierGroups.set(sk.tier_id, []);
          tierGroups.get(sk.tier_id)!.push(sk);
        }
        for (const [tierId, newSkills] of Array.from(tierGroups.entries())) {
          const usedSlots = new Set(
            section.skills
              .filter((s) => s.tier_id === tierId && !s.isDeleted && s.skill_definition_id)
              .map((s) => s.slot_index)
          );
          let slotPtr = 0;
          for (const newSkill of newSkills) {
            while (usedSlots.has(slotPtr)) slotPtr++;
            usedSlots.add(slotPtr);
            const manualUrl = newSkill.manualUrl.trim();
            await skillSettingsApi.createSkillDefinition(
              storeId,
              {
                position_id: section.positionId,
                tier_id: tierId,
                slot_index: slotPtr,
                name: newSkill.name.trim(),
                manual_url: manualUrl || null,
              },
              token
            );
            slotPtr++;
          }
        }
      }

      await fetchAllDefinitions();
      setShowEditModal(false);
      setEditSections([]);
    } catch (err) {
      setModalError(getApiErrorMessage(err, 'スキルの保存に失敗しました'));
    } finally {
      setIsSavingModal(false);
    }
  };

  // ─── CSVインポート モーダル ハンドラ ─────────────────────────────────────
  const handleOpenImport = () => {
    setImportFile(null);
    setImportResult(null);
    setImportError(null);
    setShowImportModal(true);
  };

  const handleCloseImport = () => {
    if (isImporting) return;
    setShowImportModal(false);
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await skillSettingsApi.downloadImportTemplate(storeId, token);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'skill_import_template.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setImportError(getApiErrorMessage(err, 'テンプレートのダウンロードに失敗しました'));
    }
  };

  const handleImportFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImportFile(e.target.files?.[0] ?? null);
    setImportResult(null);
    setImportError(null);
  };

  const handleRunImport = async () => {
    if (!importFile || !storeId) return;
    setIsImporting(true);
    setImportError(null);
    setImportResult(null);
    try {
      const result = await skillSettingsApi.importSkillsCsv(storeId, importFile, token);
      setImportResult(result);
      if (result.success_count > 0) {
        await fetchAllDefinitions();
      }
    } catch (err) {
      setImportError(getApiErrorMessage(err, 'CSVインポートに失敗しました'));
    } finally {
      setIsImporting(false);
    }
  };

  const loading = loadingPositions || loadingTiers || isLoading;

  const renderPositionMenuItems = (excludeIds: string[] = []) => {
    const grouped = positions.reduce<Record<string, JobPositionWithIndustry[]>>((acc, p) => {
      const n = p.industry?.name || '未分類';
      if (!acc[n]) acc[n] = [];
      acc[n].push(p);
      return acc;
    }, {});
    return Object.entries(grouped).flatMap(([industryName, list]) => [
      <ListSubheader key={`hdr-${industryName}`} sx={{ bgcolor: '#f5f5f5', fontWeight: 700, color: '#555', lineHeight: '36px' }}>
        {industryName}
      </ListSubheader>,
      ...list
        .filter((p) => !excludeIds.includes(p.id.toString()))
        .map((p) => (
          <MenuItem key={p.id} value={p.id.toString()}>{p.name}</MenuItem>
        )),
    ]);
  };

  // ─── レンダリング ─────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f8f9' }}>
      <PageHeader
        title="スキル設定"
        icon={<SvgIcon component={SidebarCrewMgmtIcon} inheritViewBox sx={{ color: '#898989', fontSize: 22 }} />}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              size="small"
              onClick={handleOpenImport}
              disabled={loading || positions.length === 0}
              sx={{
                bgcolor: '#f0f0f0',
                color: '#555',
                borderRadius: '20px',
                px: 2,
                py: 0.6,
                fontWeight: 600,
                fontSize: '0.8rem',
                boxShadow: 'none',
                mr: 1,
                '&:hover': { bgcolor: '#e0e0e0' },
                '&.Mui-disabled': { bgcolor: '#f0f0f0', color: '#aaa' },
              }}
            >
              CSVインポート
            </Button>
            <Button
              size="small"
              onClick={handleOpenEdit}
              disabled={loading || positions.length === 0}
              sx={{
                bgcolor: '#e3f0ff',
                color: PRIMARY_BLUE,
                borderRadius: '20px',
                px: 2,
                py: 0.6,
                fontWeight: 600,
                fontSize: '0.8rem',
                boxShadow: 'none',
                mr: 1.5,
                '&:hover': { bgcolor: '#cce3ff' },
                '&.Mui-disabled': { bgcolor: '#f0f0f0', color: '#aaa' },
              }}
            >
              追加
            </Button>
          </Box>
        }
      />

      <Box sx={{ pb: { xs: 2, sm: 3 } }}>
        {error && (
          <Alert severity="error" sx={{ mx: { xs: 2, sm: 3 }, mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : positions.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography color="text.secondary">
              職種が登録されていません。先に職種を作成してください。
            </Typography>
          </Box>
        ) : (
          /* スキルが登録された職種のみ表示 */
          <Box>
            {positions.filter((pos) => (defsByPositionId[pos.id.toString()] ?? []).length > 0).map((pos) => {
              const posDefs = defsByPositionId[pos.id.toString()] ?? [];
              return (
                <React.Fragment key={pos.id}>
                  <Divider />
                  {/* 職種ブロック */}
                  <Box sx={{ px: 2, pt: 1.25, pb: 1.25 }}>
                    <Typography variant="caption" sx={{ color: '#898989', display: 'block' }}>
                      職種
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#3c3c3c', mt: 0.25 }}>
                      {pos.name}
                    </Typography>
                  </Box>
                  {/* ランクブロック（縦積み） */}
                  {tiers.map((tier) => {
                    const tierDefs = posDefs
                      .filter((d) => d.tier?.tier_id === tier.tier_id)
                      .sort((a, b) => (a.slot_index ?? 0) - (b.slot_index ?? 0));
                    return (
                      <React.Fragment key={tier.tier_id}>
                        <Divider />
                        <Box sx={{ px: 2, pt: 1.25, pb: 1.25 }}>
                          {/* ランクバッジ + ランク名（⭐️フォント: caption, gray） */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                            <RankStar tierCode={tier.code} color={tier.color} size={16} />
                            <Typography variant="caption" sx={{ color: '#898989' }}>
                              {tier.name}
                            </Typography>
                          </Box>
                          {/* スキル名（非⭐️フォント: body1, dark）または "-" */}
                          {tierDefs.length > 0 ? (
                            tierDefs.map((d) => (
                              <Box key={d.skill_definition_id} sx={{ mb: 0.25 }}>
                                <Typography variant="body1" sx={{ color: '#3c3c3c' }}>
                                  {d.name}
                                </Typography>
                                {d.manual_url && (
                                  <Typography
                                    component="a"
                                    href={d.manual_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="caption"
                                    sx={{
                                      color: PRIMARY_BLUE,
                                      textDecoration: 'underline',
                                      wordBreak: 'break-all',
                                      display: 'block',
                                    }}
                                  >
                                    {d.manual_url}
                                  </Typography>
                                )}
                              </Box>
                            ))
                          ) : (
                            <Typography variant="body1" sx={{ color: '#c4c4c4', fontStyle: 'italic' }}>
                              -
                            </Typography>
                          )}
                        </Box>
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}
            <Divider />
          </Box>
        )}
      </Box>

      {/* ===== 編集モーダル（画面上部まで伸びるハーフモーダル） ===== */}
      <Drawer
        anchor="bottom"
        open={showEditModal}
        onClose={handleCloseEdit}
        PaperProps={{
          sx: {
            borderRadius: '16px 16px 0 0',
            height: '100%',
            maxHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* ドラッグハンドル */}
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5, pb: 0.5, flexShrink: 0 }}>
          <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: '#c4c4c4' }} />
        </Box>

        {/* ヘッダー（中央揃え） */}
        <Box sx={{ px: 2, pb: 1.5, flexShrink: 0, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#3c3c3c', mb: 0.5 }}>
            スキル設定
          </Typography>
          <Typography variant="body2" sx={{ color: '#898989' }}>
            それぞれの職種に習得スキルを設定
          </Typography>
        </Box>
        <Divider />

        {/* スクロール可能コンテンツ */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pt: 2, pb: { xs: 5, sm: 1 } }}>
          {modalError && (
            <Alert severity="error" sx={{ mb: 2 }}>{modalError}</Alert>
          )}

          {/* 職種セクション */}
          {editSections.map((section, secIdx) => {
            const usedPids = editSections
              .filter((s) => s.sectionId !== section.sectionId)
              .map((s) => s.positionId)
              .filter(Boolean);

            return (
              <Box
                key={section.sectionId}
                sx={{ mb: 3, ...(secIdx > 0 ? { pt: 2, borderTop: '1px solid #f0f0f0' } : {}) }}
              >
                {/* 職種セレクト */}
                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600, color: '#3c3c3c', fontSize: '0.875rem' }}>
                    職種
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={section.positionId}
                      onChange={(e) => handleSectionPositionChange(section.sectionId, e)}
                      displayEmpty
                      disabled={isSavingModal}
                      sx={{
                        bgcolor: '#fff',
                        borderRadius: '10px',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e7e7e7' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#c4c4c4' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY_BLUE },
                      }}
                    >
                      <MenuItem value="" disabled>
                        <Typography sx={{ color: '#aaa' }}>職種を選択してください</Typography>
                      </MenuItem>
                      {/* 他セクションで使用中の職種を除外。ただし自分自身は表示 */}
                      {renderPositionMenuItems(usedPids)}
                    </Select>
                  </FormControl>
                </Box>

                {/* ランク別スキル編集 */}
                {tiers.map((tier) => {
                  const activeTierSkills = section.skills.filter(
                    (s) => s.tier_id === tier.tier_id && !s.isDeleted
                  );

                  return (
                    <Box key={tier.tier_id} sx={{ mb: 2.5 }}>
                      <Box sx={{ mb: 1 }}>
                        <RankStar tierCode={tier.code} color={tier.color} label={tier.name} showLabel size={20} />
                      </Box>

                      {activeTierSkills.map((skill) => {
                        const urlInvalid = isManualUrlInvalid(skill.manualUrl);
                        return (
                          <Box key={skill.localId} sx={{ mb: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <TextField
                                value={skill.name}
                                onChange={(e) => handleSkillNameChange(section.sectionId, skill.localId, e.target.value)}
                                fullWidth
                                size="small"
                                placeholder="スキル名を入力"
                                disabled={isSavingModal}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    bgcolor: '#fff',
                                    borderRadius: '10px',
                                    '& fieldset': { borderColor: '#e7e7e7' },
                                    '&:hover fieldset': { borderColor: '#c4c4c4' },
                                    '&.Mui-focused fieldset': { borderColor: PRIMARY_BLUE },
                                  },
                                }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteSkill(section.sectionId, skill.localId)}
                                disabled={isSavingModal}
                                sx={{ color: '#dc2626', flexShrink: 0 }}
                              >
                                <SvgIcon component={TrashIcon} inheritViewBox sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Box>
                            <TextField
                              value={skill.manualUrl}
                              onChange={(e) => handleSkillManualUrlChange(section.sectionId, skill.localId, e.target.value)}
                              fullWidth
                              size="small"
                              placeholder="マニュアルURL（任意 / https://...）"
                              disabled={isSavingModal}
                              error={urlInvalid}
                              helperText={urlInvalid ? 'http:// または https:// で始めてください' : ''}
                              sx={{
                                ml: 0,
                                '& .MuiOutlinedInput-root': {
                                  bgcolor: '#fff',
                                  borderRadius: '10px',
                                  fontSize: '0.85rem',
                                  '& fieldset': { borderColor: '#e7e7e7' },
                                  '&:hover fieldset': { borderColor: '#c4c4c4' },
                                  '&.Mui-focused fieldset': { borderColor: PRIMARY_BLUE },
                                },
                              }}
                            />
                          </Box>
                        );
                      })}

                      <AddItemButton
                        label="スキルを追加"
                        onClick={() => handleAddSkillToTier(section.sectionId, tier)}
                        sx={{ mt: 0.5, ...(isSavingModal ? { opacity: 0.5, pointerEvents: 'none' } : {}) }}
                      />
                    </Box>
                  );
                })}
              </Box>
            );
          })}

          {/* 職種を追加するボタン（スクロール領域末尾・全幅・水色・枠なし） */}
          <Box sx={{ mt: 1, mb: 2 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleAddPositionSection}
              disabled={isSavingModal}
              sx={{
                borderRadius: 2,
                py: 1.5,
                bgcolor: '#e3f0ff',
                color: PRIMARY_BLUE,
                boxShadow: 'none',
                fontWeight: 600,
                '&:hover': { bgcolor: '#cce3ff', boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: '#f0f0f0', color: '#aaa' },
              }}
            >
              + 職種を追加する
            </Button>
          </Box>
        </Box>

        {/* フッターボタン */}
        <Box sx={{ px: 2, pb: 3, pt: 1.5, flexShrink: 0, borderTop: '1px solid #f0f0f0' }}>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="contained"
              onClick={handleCloseEdit}
              disabled={isSavingModal}
              sx={{
                width: 90,
                flexShrink: 0,
                borderRadius: 2,
                py: 1.4,
                fontWeight: 700,
                fontSize: '0.95rem',
                bgcolor: '#e0e0e0',
                color: '#555',
                boxShadow: 'none',
                whiteSpace: 'nowrap',
                '&:hover': { bgcolor: '#d0d0d0', boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: '#f0f0f0', color: '#aaa' },
              }}
            >
              戻る
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSaveModal}
              disabled={isSavingModal || !storeId}
              sx={{
                bgcolor: PRIMARY_BLUE,
                borderRadius: 2,
                py: 1.4,
                fontWeight: 700,
                fontSize: '0.95rem',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#0062c4', boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: '#c4c4c4', color: '#fff' },
              }}
            >
              {isSavingModal ? <CircularProgress size={22} color="inherit" /> : '保存する'}
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* ===== CSVインポートモーダル ===== */}
      <Drawer
        anchor="bottom"
        open={showImportModal}
        onClose={handleCloseImport}
        PaperProps={{
          sx: {
            borderRadius: '16px 16px 0 0',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5, pb: 0.5, flexShrink: 0 }}>
          <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: '#c4c4c4' }} />
        </Box>

        <Box sx={{ px: 2, pb: 1.5, flexShrink: 0, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#3c3c3c', mb: 0.5 }}>
            スキルCSV一括登録
          </Typography>
          <Typography variant="body2" sx={{ color: '#898989' }}>
            職種・ランク・スキル名・マニュアルURLをCSVで一括登録します
          </Typography>
        </Box>
        <Divider />

        <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pt: 2, pb: { xs: 5, sm: 2 } }}>
          {importError && (
            <Alert severity="error" sx={{ mb: 2 }}>{importError}</Alert>
          )}

          <Button
            variant="outlined"
            fullWidth
            onClick={handleDownloadTemplate}
            sx={{ mb: 2, borderRadius: 2, py: 1.2, fontWeight: 600 }}
          >
            テンプレートをダウンロード
          </Button>

          <Button
            variant="outlined"
            component="label"
            fullWidth
            disabled={isImporting}
            sx={{ mb: 1, borderRadius: 2, py: 1.2, fontWeight: 600 }}
          >
            {importFile ? importFile.name : 'CSVファイルを選択'}
            <input type="file" accept=".csv" hidden onChange={handleImportFileChange} />
          </Button>

          {importResult && (
            <Box sx={{ mt: 2 }}>
              <Alert severity={importResult.error_count > 0 ? 'warning' : 'success'} sx={{ mb: 1.5 }}>
                {`成功 ${importResult.success_count}件 / エラー ${importResult.error_count}件（全${importResult.total}件）`}
              </Alert>
              {importResult.rows
                .filter((r) => r.status === 'error')
                .map((r) => (
                  <Typography key={r.row} variant="body2" sx={{ color: '#dc2626', mb: 0.5 }}>
                    {`${r.row}行目: ${r.error}`}
                  </Typography>
                ))}
            </Box>
          )}
        </Box>

        <Box sx={{ px: 2, pb: 3, pt: 1.5, flexShrink: 0, borderTop: '1px solid #f0f0f0' }}>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="contained"
              onClick={handleCloseImport}
              disabled={isImporting}
              sx={{
                width: 90,
                flexShrink: 0,
                borderRadius: 2,
                py: 1.4,
                fontWeight: 700,
                fontSize: '0.95rem',
                bgcolor: '#e0e0e0',
                color: '#555',
                boxShadow: 'none',
                whiteSpace: 'nowrap',
                '&:hover': { bgcolor: '#d0d0d0', boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: '#f0f0f0', color: '#aaa' },
              }}
            >
              閉じる
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleRunImport}
              disabled={isImporting || !importFile || !storeId}
              sx={{
                bgcolor: PRIMARY_BLUE,
                borderRadius: 2,
                py: 1.4,
                fontWeight: 700,
                fontSize: '0.95rem',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#0062c4', boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: '#c4c4c4', color: '#fff' },
              }}
            >
              {isImporting ? <CircularProgress size={22} color="inherit" /> : 'インポート実行'}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default SkillSettings;
